import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { useAuthStore } from '../../store/authStore';
import BrandHeader from '../../components/brand/BrandHeader';

type AccountType = 'brand' | 'creator' | null;

type BrandProfile = {
  id: string;
  user_id: string;
  company_name: string;
  description?: string;
  website?: string;
  industry?: string;
  logo_url?: string;
  contact_email?: string;
  contact_phone?: string;
};

const INDUSTRIES = [
  'Technology',
  'E-commerce',
  'Fashion & Apparel',
  'Food & Beverage',
  'Health & Wellness',
  'Beauty & Cosmetics',
  'Automotive',
  'Finance & Banking',
  'Real Estate',
  'Travel & Hospitality',
  'Entertainment & Media',
  'Sports & Fitness',
  'Education',
  'Gaming',
  'Home & Garden',
  'Pet Care',
  'Electronics',
  'Retail',
  'Professional Services',
  'Non-Profit',
  'Other'
];

export default function EditProfile() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [account_type, set_account_type] = useState<AccountType>(null);
  const [saving_type, set_saving_type] = useState(false);
  const [show_saved_modal, set_show_saved_modal] = useState(false);
  
  const [formData, setFormData] = useState<BrandProfile>({
    id: '',
    user_id: '',
    company_name: '',
    description: '',
    website: '',
    industry: '',
    logo_url: '',
    contact_email: '',
    contact_phone: '',
  });
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [logoUrlInput, setLogoUrlInput] = useState('');
  const [logoUrlError, setLogoUrlError] = useState('');
  const [savingLogoUrl, setSavingLogoUrl] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const result = await back_app.bprofile_get();
      if (!result.err && result) {
        setFormData(result);
      }
      if (user?.email) {
        let org_res = await back_app.org_get();
        if (org_res?.flag?.is_brand)
          set_account_type('brand');
        else if (org_res?.flag?.is_creator)
          set_account_type('creator');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      if ((error as Error)?.message == 'no session') navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const account_type_set = async (type: AccountType) => {
    if (!type || type == account_type) return;
    set_saving_type(true);
    try {
      let is_brand = type == 'brand';
      let res1 = await back_app.org_set('flag.is_brand', is_brand);
      let res2 = await back_app.org_set('flag.is_creator', !is_brand);
      console.log('account_type_set', type, res1, res2);
      if (!res1?.err && !res2?.err) {
        set_account_type(type);
        localStorage.setItem('user_type', type);
      }
    } catch (e) {
      console.error('account_type_set failed', e);
    }
    set_saving_type(false);
  };

  const handleUseLogoUrl = async () => {
    setLogoUrlError('');
    const url = logoUrlInput.trim();
    if (!url) {
      setLogoUrlError('Enter a URL');
      return;
    }
    if (!/^https?:\/\//u.test(url)) {
      setLogoUrlError('URL must start with http:// or https://');
      return;
    }
    setSavingLogoUrl(true);
    try {
      const result = await back_app.org_logo_from_url(url);
      if (result?.err) {
        setLogoUrlError(`Failed: ${result.err}`);
        setSavingLogoUrl(false);
        return;
      }
      const s3_url = result.logo_url || result.url || '';
      if (!s3_url) {
        setLogoUrlError('No URL returned');
        setSavingLogoUrl(false);
        return;
      }
      setFormData(prev => ({...prev, logo_url: s3_url}));
      setLogoUrlInput('');
      setLogoModalOpen(false);
    } catch (err) {
      console.error('logo url upload failed', err);
      setLogoUrlError('Upload failed');
    } finally {
      setSavingLogoUrl(false);
    }
  };

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await handleLogoUpload(e);
    setLogoModalOpen(false);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG, JPG)');
      return;
    }

    
    if (file.size > 2 * 1024 * 1024) {
      alert('Logo must be less than 2MB');
      return;
    }

    setUploadingLogo(true);

    try {
      const form = new FormData();
      form.append('file', file);
      const result = await back_app.org_logo_upload(form);
      if (result.err)
        throw new Error(result.err);
      setFormData(prev => ({...prev, logo_url: result.logo_url || result.url || ''}));
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Error uploading logo. Please try again.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }

    if (formData.website && !formData.website.startsWith('http')) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSaving(true);

    try {
      const result = await back_app.bprofile_set({
          company_name: formData.company_name,
          description: formData.description,
          website: formData.website,
          industry: formData.industry,
          logo_url: formData.logo_url,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
      });

      if (!result.err) {
        set_show_saved_modal(true);
      } else {
        alert('Error: ' + (result.err || 'Failed to update profile'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <button
          onClick={() => navigate('/brand/profile')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <span>←</span>
          <span>Back to Profile</span>
        </button>

        {}
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Edit Profile</h2>
          <p className="text-gray-600 mt-1">Update your brand information</p>
        </div>

        {}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border shadow-sm p-4 sm:p-6 lg:p-8 space-y-6">
          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Brand Logo
            </label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg flex-shrink-0">
                {formData.logo_url ? (
                  <img src={formData.logo_url} alt="Logo" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center">
                    <span className="text-3xl text-white font-bold">
                      {formData.company_name ? formData.company_name.charAt(0).toUpperCase() : '?'}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => { setLogoUrlError(''); setLogoModalOpen(true); }}
                  disabled={uploadingLogo || savingLogoUrl}
                  className={`inline-block px-6 py-3 border-2 border-gray-300 rounded-lg
                    text-sm font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer
                    transition-all ${
                      uploadingLogo || savingLogoUrl ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  {uploadingLogo || savingLogoUrl ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent
                        rounded-full animate-spin"/>
                      {uploadingLogo ? 'Uploading...' : 'Fetching...'}
                    </span>
                  ) : 'Change Logo'}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  PNG or JPG • Max 2MB • Recommended: Square 512×512px
                </p>
              </div>
            </div>
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              placeholder="e.g., Acme Corporation"
              required
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.company_name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.company_name && (
              <p className="text-red-500 text-sm mt-1">{errors.company_name}</p>
            )}
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Industry
            </label>
            <select
              value={formData.industry || ''}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select Industry</option>
              {INDUSTRIES.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Helps creators understand your brand category
            </p>
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Account Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                disabled={saving_type}
                onClick={() => account_type_set('brand')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 font-semibold transition-all ${
                  account_type == 'brand'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                } ${saving_type ? 'opacity-50' : ''}`}
              >
                Brand
                <p className="text-xs font-normal mt-1">Create campaigns, manage assets</p>
              </button>
              <button
                type="button"
                disabled={saving_type}
                onClick={() => account_type_set('creator')}
                className={`flex-1 px-4 py-3 rounded-lg border-2 font-semibold transition-all ${
                  account_type == 'creator'
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                } ${saving_type ? 'opacity-50' : ''}`}
              >
                Creator
                <p className="text-xs font-normal mt-1">Upload videos, earn from sponsorships</p>
              </button>
            </div>
            {saving_type && (
              <p className="text-xs text-gray-500 mt-1 animate-pulse">Saving account type...</p>
            )}
            {!account_type && !saving_type && (
              <p className="text-xs text-red-500 mt-1">Please select an account type</p>
            )}
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Company Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              placeholder="Tell creators about your brand, mission, and values..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.description?.length || 0} characters
            </p>
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Website
            </label>
            <input
              type="url"
              value={formData.website || ''}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://example.com"
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.website ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.website && (
              <p className="text-red-500 text-sm mt-1">{errors.website}</p>
            )}
          </div>

          {}
          <div className="pt-6 border-t">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
            
            <div className="space-y-6">
              {}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={formData.contact_email || ''}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="contact@example.com"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.contact_email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.contact_email && (
                  <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>
                )}
              </div>

              {}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone || ''}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1 text-sm">Profile Tips</h4>
                <ul className="text-xs text-gray-700 space-y-1">
                  <li>• A professional logo increases creator trust</li>
                  <li>• Complete profiles get 3x more creator applications</li>
                  <li>• Contact info helps creators reach you for collaborations</li>
                </ul>
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/brand/profile')}
              className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || uploadingLogo}
              className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>

      {logoModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center
          justify-center z-50 p-4"
          onClick={() => { if (!uploadingLogo && !savingLogoUrl) setLogoModalOpen(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Change Logo</h3>
              <button
                type="button"
                onClick={() => setLogoModalOpen(false)}
                disabled={uploadingLogo || savingLogoUrl}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none
                  disabled:opacity-50"
              >×</button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Upload from your device
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChosen}
                  className="hidden"
                  id="logo-upload-modal"
                  disabled={uploadingLogo || savingLogoUrl}
                />
                <label
                  htmlFor="logo-upload-modal"
                  className={`block w-full text-center px-4 py-3 border-2 border-dashed
                    border-gray-300 rounded-lg text-sm font-semibold text-gray-700
                    hover:bg-gray-50 cursor-pointer transition-all ${
                      uploadingLogo || savingLogoUrl
                        ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  {uploadingLogo ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent
                        rounded-full animate-spin"/>
                      Uploading...
                    </span>
                  ) : 'Choose file'}
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  PNG or JPG • Max 2MB
                </p>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-gray-200"/>
                <span className="text-xs text-gray-400 uppercase tracking-wide">or</span>
                <div className="flex-1 h-px bg-gray-200"/>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Provide a URL
                </label>
                <input
                  type="url"
                  value={logoUrlInput}
                  onChange={(e) => { setLogoUrlInput(e.target.value); setLogoUrlError(''); }}
                  placeholder="https://example.com/logo.png"
                  disabled={savingLogoUrl || uploadingLogo}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2
                    focus:ring-blue-500 focus:border-transparent ${
                      logoUrlError ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {logoUrlError && (
                  <p className="text-red-500 text-xs mt-1">{logoUrlError}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  We'll download and store the image on our server.
                </p>
                <button
                  type="button"
                  onClick={handleUseLogoUrl}
                  disabled={savingLogoUrl || uploadingLogo || !logoUrlInput.trim()}
                  className="mt-3 w-full px-4 py-2 bg-gradient-to-r from-blue-500
                    to-purple-500 text-white rounded-lg text-sm font-semibold
                    hover:shadow-lg transition-all disabled:opacity-50
                    disabled:cursor-not-allowed"
                >
                  {savingLogoUrl ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent
                        rounded-full animate-spin"/>
                      Fetching & uploading...
                    </span>
                  ) : 'Use URL'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {show_saved_modal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 sm:p-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mb-4 shadow-lg">
              <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Profile updated
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Your changes have been saved.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => set_show_saved_modal(false)}
                className="w-full sm:flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
              >
                Stay here
              </button>
              <button
                onClick={() => { set_show_saved_modal(false); navigate('/brand/profile'); }}
                className="w-full sm:flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Back to Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}